import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { extractPhone } from "./WhatsAppChatButton";

/**
 * Bottone verde "Avvia chat WhatsApp" che apre un dialog per iniziare una nuova
 * sessione WhatsApp DENTRO la piattaforma. Chiama /api/whatsapp/open-chat che
 * invia un template Meta pre-approvato → apre la finestra di 24h → il tecnico
 * può poi rispondere free-form dal blocco standard "Rispondi via WhatsApp".
 *
 * Precompila il numero da reporter_contact / reported_by / dispenser end_customer_phone.
 */
export function WhatsAppOpenChatButton({ ticket, onOpened, className = "", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    const guess =
      extractPhone(ticket?.reporter_contact) ||
      extractPhone(ticket?.reported_by) ||
      extractPhone(ticket?.dispenser?.end_customer_phone) ||
      extractPhone(ticket?.dispenser?.customer_phone) ||
      "";
    setPhone(guess);
    setOpen(true);
  };

  const send = async () => {
    const digits = phone.replace(/\D/g, "").replace(/^00/, "");
    if (digits.length < 8) {
      toast.error("Inserisci un numero telefonico valido (con prefisso internazionale)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/open-chat", { ticket_id: ticket.id, phone: digits });
      toast.success(`Chat WhatsApp avviata con +${data.phone}. Ora puoi scrivere liberamente.`);
      setOpen(false);
      if (onOpened) await onOpened();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={openDialog}
        size={size}
        data-testid="wa-open-chat-btn"
        className={`bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm ${className}`}
      >
        <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
        Avvia chat WhatsApp
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="wa-open-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Avvia chat WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Numero del cliente *</Label>
              <Input
                data-testid="wa-open-chat-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 333 1234567"
                className="mt-1 rounded-sm font-mono"
              />
              <div className="mt-1 text-[11px] text-zinc-500">
                Formato internazionale (con prefisso, es. +39 per Italia). Spazi e trattini ammessi.
              </div>
            </div>
            <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <div>
                  Verrà inviato un breve <b>messaggio di apertura</b> approvato da Meta per attivare la conversazione.
                </div>
                <div>
                  Da quel momento potrai scrivere liberamente al cliente dalla piattaforma per le successive 24 ore, e le sue risposte compariranno automaticamente nel timeline del ticket.
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
            <Button
              data-testid="wa-open-chat-send"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              onClick={send}
              disabled={busy || !phone.trim()}
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio...</> : "Avvia chat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
